import type { DesignSpec, ImageAnalysis } from '@/types';
import { analyzeImage } from './analyzer';
import { buildVariants, reflow } from './layout/layoutEngine';
import { claudeProvider, selectProvider } from './ai';
import { store, currentBrief } from '@/state/store';
import { downscaleDataUrl, loadImage } from '@/lib/utils';
import { prepareMeasurer, type CanvasMeasurer } from './render/measurer';
import { HN_WEIGHTS } from '@/lib/fonts';

/**
 * The one place the analyzer, the AI layer, and the layout engine are wired
 * together. Components call these three functions and nothing else, which is
 * what keeps the design engine independent of the UI.
 */

/** Max edge sent to the vision model — see downscaleDataUrl for the reasoning. */
const VISION_MAX_EDGE = 1280;

const imageCache = new Map<string, HTMLImageElement>();

/**
 * Real glyph metrics for the layout engine. Built once the fonts have loaded,
 * so the fitter measures with the same face the renderer draws with.
 */
let measurer: CanvasMeasurer | undefined;

async function getMeasurer(): Promise<CanvasMeasurer | undefined> {
  if (!measurer) {
    measurer = await prepareMeasurer(HN_WEIGHTS.map((w) => w.value));
  }
  return measurer;
}

export async function getImage(dataUrl: string): Promise<HTMLImageElement> {
  const cached = imageCache.get(dataUrl);
  if (cached) return cached;
  const img = await loadImage(dataUrl);
  imageCache.set(dataUrl, img);
  return img;
}

export async function checkAIAvailability(): Promise<void> {
  const configured = await claudeProvider.probe();
  store.set({ aiConfigured: configured });
}

/** Analyze a newly uploaded photo. Cheap, local, and always runs. */
export async function runAnalysis(dataUrl: string): Promise<ImageAnalysis> {
  store.set({ status: 'analyzing', statusMessage: 'Reading the image…' });
  const img = await getImage(dataUrl);
  const analysis = await analyzeImage(img);
  store.set({
    analysis,
    status: 'idle',
    statusMessage: describeAnalysis(analysis),
  });
  return analysis;
}

function describeAnalysis(a: ImageAnalysis): string {
  const bits: string[] = [];
  bits.push(`${a.width}×${a.height}`);
  if (a.faces.length) {
    bits.push(
      `${a.faces.length} face${a.faces.length > 1 ? 's' : ''} (${a.faces[0].source})`,
    );
  }
  bits.push(a.busy ? 'busy background' : 'clean background');
  bits.push(`best zone: ${a.zones[0]?.label.toLowerCase()}`);
  return bits.join(' · ');
}

/**
 * Full generate: analyze if needed, ask the design intelligence for a
 * directive, then build four distinct compositions.
 */
export async function generateDesigns(seed = Date.now() % 100000): Promise<void> {
  const state = store.getState();
  if (!state.photoDataUrl) {
    store.set({ status: 'error', statusMessage: 'Add a photo first.' });
    return;
  }
  if (!state.title.trim()) {
    store.set({ status: 'error', statusMessage: 'Add a headline first.' });
    return;
  }

  try {
    const analysis = state.analysis ?? (await runAnalysis(state.photoDataUrl));
    const brief = currentBrief(store.getState());
    const provider = selectProvider(state.preferAI);

    store.set({
      status: 'generating',
      statusMessage:
        provider.id === 'claude'
          ? 'Claude is reading the composition…'
          : 'Composing layouts…',
    });

    // Only the vision path needs a downscaled copy; the heuristic engine works
    // off measurements it already has.
    const payloadImage =
      provider.id === 'claude'
        ? await downscaleDataUrl(await getImage(state.photoDataUrl), VISION_MAX_EDGE)
        : state.photoDataUrl;

    const directive = await provider.analyze(payloadImage, analysis, brief);
    const variants = buildVariants(analysis, brief, directive, 4, seed, await getMeasurer());

    store.commit({
      directive,
      variants,
      activeId: variants[0]?.id ?? null,
      selectedBlockId: null,
      status: 'ready',
      usedProvider: provider.label,
      statusMessage: `${variants.length} layouts from ${provider.label.toLowerCase()}.`,
    });
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    store.set({ status: 'error', statusMessage: message });
  }
}

/** Re-roll compositions from the existing directive — no second API call. */
export async function redesign(): Promise<void> {
  const state = store.getState();
  if (!state.analysis || !state.directive) {
    await generateDesigns();
    return;
  }
  const brief = currentBrief(state);
  const variants = buildVariants(
    state.analysis,
    brief,
    state.directive,
    4,
    Math.floor(Math.random() * 100000),
    await getMeasurer(),
  );
  store.commit({
    variants,
    activeId: variants[0]?.id ?? null,
    selectedBlockId: null,
    status: 'ready',
    statusMessage: 'New compositions from the same analysis.',
  });
}

/**
 * Re-run layout for every variant against a new format. Keeps each variant's
 * recipe so the four options stay recognisably themselves.
 */
export async function applyFormat(formatId: DesignSpec['format']): Promise<void> {
  const state = store.getState();
  if (!state.analysis || !state.directive || state.variants.length === 0) {
    store.commit({ format: formatId });
    return;
  }
  const measure = await getMeasurer();
  const brief = { ...currentBrief(state), format: formatId };
  const variants = state.variants.map((v) =>
    reflow({ ...v, format: formatId }, state.analysis!, brief, state.directive!, measure),
  );
  store.commit({
    format: formatId,
    variants,
    activeId: variants.find((v) => v.id === state.activeId)?.id ?? variants[0]?.id ?? null,
    statusMessage: 'Re-composed for the new canvas size.',
  });
}

/** Re-run layout after a text or style change, preserving each recipe. */
export async function refreshLayout(): Promise<void> {
  const state = store.getState();
  if (!state.analysis || !state.directive || state.variants.length === 0) return;
  const measure = await getMeasurer();
  const brief = currentBrief(state);
  const variants = state.variants.map((v) =>
    reflow(v, state.analysis!, brief, state.directive!, measure),
  );
  store.commit({ variants, statusMessage: 'Layout updated.' });
}
